import {
  PublicClientApplication,
  AccountInfo,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { msalConfig, loginScopes } from "./msal-config";
import type { BolsterupUser, Company, TokenExchangeResponse } from "@/types";

const AUTH_BASE = import.meta.env.VITE_AUTH_BASE_URL;
const API_BASE = import.meta.env.VITE_API_BASE_URL;

let msalInstance: PublicClientApplication | null = null;

async function getMsalInstance(): Promise<PublicClientApplication> {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
  }
  return msalInstance;
}

/**
 * Attempt SSO via Nested App Authentication (NAA).
 * Flow: ssoSilent → acquireTokenSilent (cached) → popup fallback.
 */
async function acquireMicrosoftToken(): Promise<string> {
  const pca = await getMsalInstance();

  // 1. Try SSO silent (NAA broker — works even without cached accounts)
  try {
    const result = await pca.ssoSilent(loginScopes);
    console.log("[Auth] SSO silent succeeded");
    return result.accessToken;
  } catch (err) {
    console.warn("[Auth] SSO silent failed:", err);
  }

  // 2. Try acquireTokenSilent with cached account
  const accounts = pca.getAllAccounts();
  if (accounts.length > 0) {
    try {
      const result = await pca.acquireTokenSilent({
        ...loginScopes,
        account: accounts[0],
      });
      console.log("[Auth] Silent token acquisition succeeded");
      return result.accessToken;
    } catch (err) {
      console.warn("[Auth] Silent token acquisition failed:", err);
    }
  }

  // 3. Fall back to popup
  console.log("[Auth] Falling back to popup login");
  const result = await pca.acquireTokenPopup(loginScopes);
  return result.accessToken;
}

/**
 * Exchange Microsoft token for Bolsterup Cognito credentials.
 * This calls the new backend endpoint that validates the Microsoft JWT,
 * looks up the user by email, and returns Cognito tokens.
 */
async function exchangeForBolsterupTokens(
  microsoftToken: string
): Promise<TokenExchangeResponse> {
  const res = await fetch(`${AUTH_BASE}/auth/exchange-microsoft-token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ microsoftToken }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(
      body.message || `Token exchange failed (${res.status})`
    );
  }

  return res.json();
}

/**
 * Set the active company for the authenticated user.
 */
async function setActiveCompany(
  accessToken: string,
  companyId: string
): Promise<BolsterupUser> {
  const res = await fetch(`${API_BASE}/company/switch-active-company`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ companyId }),
  });

  if (!res.ok) {
    throw new Error("Failed to set active company");
  }

  return res.json();
}

/**
 * Full SSO login flow:
 * 1. Acquire Microsoft token (silent → Office SSO → popup)
 * 2. Exchange for Bolsterup Cognito tokens
 * 3. Return user + available companies
 */
export async function loginWithSSO(): Promise<TokenExchangeResponse> {
  const microsoftToken = await acquireMicrosoftToken();
  return exchangeForBolsterupTokens(microsoftToken);
}

export async function selectCompany(
  accessToken: string,
  companyId: string
): Promise<BolsterupUser> {
  return setActiveCompany(accessToken, companyId);
}

export async function refreshBolsterupToken(
  refreshToken: string
): Promise<{
  accessToken: string;
  sessionToken: string;
  refreshToken: string;
  newAccessTokenExpiry: number;
  expired?: boolean;
}> {
  const res = await fetch(`${AUTH_BASE}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });

  if (!res.ok) {
    throw new Error("Token refresh failed");
  }

  return res.json();
}

export function getMicrosoftAccount(): AccountInfo | null {
  if (!msalInstance) return null;
  const accounts = msalInstance.getAllAccounts();
  return accounts[0] ?? null;
}
